import { WINDOW } from './Device';

export function cloneDeep(obj: string | object | null): any {
  if (obj === null || typeof obj !== 'object') {
    // If the input is a primitive or null, return it as is
    return obj;
  }

  if (Array.isArray(obj)) {
    // If the input is an array, create a new array and clone its elements
    const newArray = [];
    for (let i = 0; i < obj.length; i++) {
      newArray[i] = cloneDeep(obj[i]);
    }
    return newArray;
  }

  // If the input is an object, create a new object and clone its properties
  const newObj: any = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      newObj[key] = cloneDeep((obj as any)[key]);
    }
  }
  return newObj;
}

export function each(collection: any, iteratee: any) {
  if (Array.isArray(collection)) {
    for (const item of collection) {
      iteratee(item);
    }
  } else {
    const keys = Object.keys(collection);
    for (const key of keys) {
      iteratee(collection[key], key);
    }
  }
}

export const pickBy = (
  object: { [x: string]: any; hasOwnProperty: (arg0: string) => any },
  predicate: (arg0: any, arg1: string) => any
) => {
  if (typeof predicate !== 'function') {
    throw new TypeError('Predicate must be a function');
  }

  const result = {} as any;

  for (const key in object) {
    if (
      Object.prototype.hasOwnProperty.call(object, key) &&
      predicate(object[key], key)
    ) {
      result[key] = object[key];
    }
  }

  return result;
};

export const omit = (obj: { [x: string]: any }, props: any[]) => {
  obj = { ...obj };
  props.forEach((prop: any) => delete obj[prop]);
  return obj;
};

export const omitBy = (
  obj: { [s: string]: any } | ArrayLike<any>,
  check: (arg0: unknown) => any
) => {
  obj = { ...obj };
  Object.entries(obj).forEach(
    ([key, value]) => check(value) && delete (obj as any)[key]
  );
  return obj;
};

export const set = (obj: any, path: string, value: any) => {
  // Regex explained: https://regexr.com/58j0k
  const pathArray = Array.isArray(path)
    ? (path as Array<string>)
    : path.match(/([^[.\]])+/g);

  pathArray?.reduce(
    (acc: { [x: string]: any }, key: string | number, i: number) => {
      if (acc[key] === undefined) acc[key] = {};
      if (i === pathArray.length - 1) acc[key] = value;
      return acc[key];
    },
    obj
  );
};

export const get = (obj: any, path: string, defValue: any) => {
  // If path is not defined or it has false value
  if (!path) return undefined;
  // Check if path is string or array. Regex : ensure that we do not have '.' and brackets.
  // Regex explained: https://regexr.com/58j0k
  const pathArray = Array.isArray(path)
    ? (path as Array<string>)
    : path.match(/([^[.\]])+/g);
  // Find value
  const result = pathArray?.reduce(
    (prevObj: { [x: string]: any }, key: string | number) =>
      prevObj && prevObj[key],
    obj
  );
  // If found value is undefined return default value; otherwise return the value
  return result === undefined ? defValue : result;
};

export const capitalize = (str: string) =>
  `${str.charAt(0).toUpperCase()}${str.slice(1).toLowerCase()}`;

/**
 *
 * @param classes -
 * @returns A string of CSS classes
 */
export const makeClass: (
  classes: Array<string | boolean | undefined>
) => string = (classes: Array<string | boolean | undefined>) =>
  classes
    .filter((e) => e)
    .join(' ')
    .trim();

/**
 * @param props - an object which has should have prop name as key and prop value as value
 * @param baseClass - a string which needs to be added as prefix to all the classes
 * @param additionalClass - a additional class that needs to be added in class list
 * @returns A array string of CSS classes
 */
export const generateClassList: (
  props: Record<string, unknown>,
  baseClass: string,
  additionalClass?: string | Array<string>
) => Array<string> = (
  props: Record<string, unknown>,
  baseClass: string,
  additionalClass?: string | Array<string>
) =>
  Object.entries(props)
    .map(([key, value]) => {
      if (typeof value === 'boolean' && value) {
        return `${baseClass}__${key}`;
      }

      if (typeof value === 'string') {
        return `${baseClass}__${key}-${value}`;
      }

      return '';
    })
    .concat(additionalClass ?? '', baseClass)
    .filter((cls) => !!cls);

/**
 *
 * @param start - array starts with
 * @param end - array ends with
 * @returns An array of numbers
 */
export const getNumberArray: (start: number, end: number) => number[] = (
  start: number,
  end: number
) => {
  return Array.from(Array(end - start).keys()).map((n) => start + n);
};

/**
 *
 * @param props - an object which has should have prop name as key and prop value as value
 * @param baseClass - a string which needs to be added as prefix to all the classes
 * @param additionalClass - a additional class that needs to be added in class list
 * @returns A string of CSS classes
 */
export const generateClass: (
  props: Record<string, unknown>,
  baseClass: string,
  additionalClass?: string | Array<string>
) => string = (
  props: Record<string, unknown>,
  baseClass: string,
  additionalClass?: string | Array<string>
) => {
  const classList = generateClassList(props, baseClass, additionalClass);
  return makeClass(classList);
};

/**
 * Checks if DOM Exist or not.
 */
export const DOMExists = !!(
  WINDOW &&
  WINDOW.document &&
  WINDOW.document.createElement
);

export type CSSVarType = {
  [key: string]: string | number | undefined;
};
export type CSSVarTypeReturn = {
  [key: string]: string | number;
};

/**
 * Returns valid CSS styles for inline use with `style` React prop.
 * @param obj - Inline CSS styles.
 * @returns Object with valid CSS style values.
 */
export const setCSSVar = (obj: CSSVarType): CSSVarTypeReturn => {
  Object.keys(obj).forEach((key) => obj[key] === undefined && delete obj[key]);

  return obj as CSSVarTypeReturn;
};

export const bindRaf = (
  fn: (...args: any) => void
): ((...args: any) => void) => {
  let isRunning = false;
  let args: any = null;

  const run = () => {
    isRunning = false;
    fn(...args);
  };

  return function (...invokingArguments: any) {
    args = invokingArguments;

    if (isRunning) {
      return;
    }

    isRunning = true;
    requestAnimationFrame(run);
  };
};
/*
 * Flattens a multi-dimensional array into one level.
 *
 * {array} lists - The array to flatten.
 *
 * @returns {array} - A flat array.
 */
export const flatten = (lists: Array<any>): Array<any> =>
  lists.reduce((a: any, b: any) => a.concat(b), []);

/**
 * Inverses the key:value pair inside object to value:key
 * @param obj
 */
export const inverseObject = (obj: object): object =>
  Object.assign({}, ...Object.entries(obj).map(([a, b]) => ({ [b]: a })));

/**
 * Tests if string doesn't exists or is empty
 * @param str
 */
export const isStrEmpty = (str: string): boolean =>
  str === undefined ||
  str === null ||
  typeof str !== 'string' ||
  !((str?.trim?.() || '').length > 0);
/**
 * Map Keyboard keys for readability
 */

export const KEYBOARD = {
  ESCAPE: 'Escape',
  ENTER: 'Enter',
  ARROWUP: 'ArrowUp',
  ARROWDOWN: 'ArrowDown',
  ARROWRIGHT: 'ArrowRight',
  ARROWLEFT: 'ArrowLeft',
  TAB: 'Tab'
};

/**
 * Delays the invocation of the function until wait milliseconds have passed since the last call of the debounced function.
 * @param fn - Function that needs to debounce
 * @param wait - time for debouncing
 * @param immediate - Can be passed to run the function at the start and then debounce subsequent calls
 * @returns Debounced Function
 */
export const debounce = (
  fn: (...args: any) => void,
  wait: number,
  immediate = false
): ((...args: any) => void) => {
  let timeoutId: NodeJS.Timeout | null;
  return function (this: any, ...args: any) {
    const later = () => {
      timeoutId = null;
      if (!immediate) fn.apply(this, args);
    };
    const callNow = immediate && !timeoutId;
    timeoutId !== null && clearTimeout(timeoutId);
    timeoutId = setTimeout(later, wait);
    if (callNow) fn.apply(this, args);
  };
};

export function spaceBeforeCapital(str: string): string {
  if (str.length < 1) return '';
  return (
    str.charAt(0).toUpperCase() +
    str
      .slice(1)
      .replace(/([A-Z])/g, ' $1')
      .trim()
  );
}

export enum ImageRatios {
  wide = 'wide',
  landscape = 'landscape',
  square = 'square',
  portrait = 'portrait'
}

export const ASPECT_RATIO_TO_PERCENTAGE_MAP = {
  [ImageRatios.wide]: '56.25%',
  [ImageRatios.landscape]: '75%',
  [ImageRatios.square]: ' 100%',
  [ImageRatios.portrait]: '125%'
};

export const PositionsList = [
  'top-start',
  'top',
  'top-end',
  'right-start',
  'right',
  'right-end',
  'bottom-start',
  'bottom',
  'bottom-end',
  'left-start',
  'left',
  'left-end'
] as const;
export type Positions = (typeof PositionsList)[number];

/**
 * Function to return modals top and left position
 * @param anchorRect - DOMRect of anchor
 * @param modalRect - DOMRect of modal
 * @param position - any position among 12 positions
 */
export function getModalPosition(
  anchorRect: DOMRect,
  modalRect: DOMRect,
  position: Positions = 'bottom'
): { top: number; left: number } {
  let top = 0;
  let left = 0;
  switch (position) {
    // top position calculation
    case 'top':
      top = anchorRect.top - modalRect.height;
      left = anchorRect.left + anchorRect.width / 2 - modalRect.width / 2;
      break;
    case 'top-start':
      top = anchorRect.top - modalRect.height;
      left = anchorRect.left;
      break;
    case 'top-end':
      top = anchorRect.top - modalRect.height;
      left = anchorRect.left + anchorRect.width - modalRect.width;
      break;
    // bottom position calculation
    case 'bottom':
      top = anchorRect.top + anchorRect.height;
      left = anchorRect.left + anchorRect.width / 2 - modalRect.width / 2;
      break;
    case 'bottom-start':
      top = anchorRect.top + anchorRect.height;
      left = anchorRect.left;
      break;
    case 'bottom-end':
      top = anchorRect.top + anchorRect.height;
      left = anchorRect.left + anchorRect.width - modalRect.width;
      break;
    // left position calculation
    case 'left':
      top = anchorRect.top + anchorRect.height / 2 - modalRect.height / 2;
      left = anchorRect.left - modalRect.width;
      break;
    case 'left-start':
      top = anchorRect.top;
      left = anchorRect.left - modalRect.width;
      break;
    case 'left-end':
      top = anchorRect.top + anchorRect.height - modalRect.height;
      left = anchorRect.left - modalRect.width;
      break;
    // right position calculation
    case 'right':
      top = anchorRect.top + anchorRect.height / 2 - modalRect.height / 2;
      left = anchorRect.left + anchorRect.width;
      break;
    case 'right-start':
      top = anchorRect.top;
      left = anchorRect.left + anchorRect.width;
      break;
    case 'right-end':
      top = anchorRect.top + anchorRect.height - modalRect.height;
      left = anchorRect.left + anchorRect.width;
      break;
  }
  return {
    top: Math.round(top >= 0 ? top : 0),
    left: Math.round(left >= 0 ? left : 0)
  };
}

export function getLineNumbers(domRef: HTMLElement): number {
  const comComputedStyles = window.getComputedStyle(domRef);
  const divHeight = +domRef.offsetHeight;
  const lineHeight = +comComputedStyles
    .getPropertyValue('line-height')
    .replace('px', '');
  const lines = divHeight / lineHeight;

  return lines;
}

export const assignDefaultValues = (
  reference: ThisType<unknown>,
  defaultValues: Record<string, unknown>
) => {
  Object.keys(defaultValues).forEach((prop) => {
    if ((reference as Record<string, unknown>)[prop] === undefined) {
      (reference as Record<string, unknown>)[prop] = defaultValues[prop];
    }
  });
  return reference;
};

export const removeBraces = (value: string): string =>
  value.replace(/[{}]/g, '');
